// apps/client/src/input/tapToWalk.ts — follows findPath waypoints (tile centres) and runs `then` on arrival (§8.3 movement).
// The path ignores other residents, so one standing on it would have you push against them for ever. A walk that makes no headway for
// tuning.move.stuckGiveUpMs of simulation time first re-plans around the residents in the way (the `detour` the owner supplies); when
// there is no other way, or it is stuck again after MAX_DETOURS detours, it gives up (M0 playtest) and runs `cancelled`, like any other
// cancelled walk. The clock counts the fixed simulation steps the walker moves by, not wall time, so a slow device (or a loaded e2e
// machine running a few frames a second) gets as many steps to slide past someone as a fast one.
import { tuning } from '@whiskerton/content';
import type { TileXY, Vec2 } from '@whiskerton/shared';

const REACHED = 0.25;      // tiles: a waypoint counts as reached within this distance (precision, not feel)
const HEADWAY = 0.1;       // tiles closer to the next waypoint than ever before on this leg: progress (precision, not feel)
const STEP_MS = 1000 / 60; // one fixed simulation step (engine/loop.ts), the stuck clock's default tick
const MAX_DETOURS = 2;     // re-plans around blocking residents per walk before it gives up (a bound, not feel)

/** A new path ([from, …, to], as findPath returns it) from where you stand that avoids the residents in the way, or null. */
export type Detour = () => readonly TileXY[] | null;

export class TapToWalk {
  private path: { x: number; y: number }[] = [];
  private then: (() => void) | null = null;
  private cancelled: (() => void) | null = null;
  private best = Infinity;
  private stuckMs = 0;
  private detours = 0;
  private readonly detour: Detour;
  constructor(detour: Detour = () => null) { this.detour = detour; }
  get active(): boolean { return this.path.length > 0; }
  /** `path` = [from, …, to] as findPath returns it; the first tile is where you stand, so walking starts at the second.
   *  `cancelled` runs instead of `then` when the walk is cancelled, replaced by a newer one or gives up (the test hooks resolve on it). */
  start(path: readonly TileXY[], then?: () => void, cancelled?: () => void): void {
    this.cancel();
    this.path = toWaypoints(path);
    this.then = then ?? null;
    this.cancelled = cancelled ?? null;
    this.detours = 0;
    this.resetHeadway();
    if (!this.path.length) this.arrive();
  }
  /** New waypoints for the walk in progress (a re-plan after a server correction): `then` and `cancelled` stay with it. A re-plan that
   *  heads for the same next waypoint keeps the stuck clock, so corrections while pushing against a resident cannot keep it from giving up. */
  reroute(path: readonly TileXY[]): void {
    if (!this.active) return;
    const next = this.path[0];
    this.path = toWaypoints(path);
    if (!next || !this.path[0] || next.x !== this.path[0].x || next.y !== this.path[0].y) this.resetHeadway();
    if (!this.path.length) this.arrive();
  }
  cancel(): void {
    const f = this.cancelled;
    this.path = []; this.then = null; this.cancelled = null;
    f?.();
  }
  /** The unit direction to the next waypoint from `pos`, or null once the last one is reached (then `then` runs) or the walk gave up
   *  for lack of headway (then `cancelled` runs). `dtMs` is the simulation time this step moves the walker by (one fixed step). */
  steer(pos: Vec2, dtMs: number = STEP_MS): { dx: number; dy: number } | null {
    while (this.path.length && Math.hypot(this.path[0]!.x - pos.x, this.path[0]!.y - pos.y) < REACHED) { this.path.shift(); this.best = Infinity; }
    if (!this.path.length) { this.arrive(); return null; }
    const d = Math.hypot(this.path[0]!.x - pos.x, this.path[0]!.y - pos.y);
    this.stuckMs += Math.max(0, dtMs);
    if (d < this.best - HEADWAY) { this.best = d; this.stuckMs = 0; }
    if (this.stuckMs >= tuning.move.stuckGiveUpMs) {
      const way = this.takeDetour();
      if (way === 'none') { this.cancel(); return null; }
      if (way === 'here') { this.path = []; this.arrive(); return null; }
    }
    const t = this.path[0]!, n = Math.hypot(t.x - pos.x, t.y - pos.y);
    return { dx: (t.x - pos.x) / n, dy: (t.y - pos.y) / n };
  }
  /** Stuck: switches to a path around the residents in the way ('detour'). 'here' when that path ends where you stand (you are already
   *  next to a goal someone else now stands on: the walk arrives). 'none' when there is no way round, it would head for the same next
   *  waypoint, or the walk already used its detours. */
  private takeDetour(): 'detour' | 'here' | 'none' {
    if (this.detours >= MAX_DETOURS) return 'none';
    const p = this.detour();
    if (!p || !p.length) return 'none';
    const way = toWaypoints(p), next = this.path[0]!;
    if (!way.length) return 'here';
    if (way[0]!.x === next.x && way[0]!.y === next.y) return 'none';
    this.detours++;
    this.path = way;
    this.resetHeadway();
    return 'detour';
  }
  private resetHeadway(): void { this.best = Infinity; this.stuckMs = 0; }
  private arrive(): void { const f = this.then; this.then = null; this.cancelled = null; f?.(); }
}

const toWaypoints = (path: readonly TileXY[]) => path.slice(1).map((t) => ({ x: t.x + 0.5, y: t.y + 0.5 }));
